/**
 * The hangar without the showcase scene: the Phase 5 catalog with what the account owns and
 * what the rest costs, and one buy button for the selected locked mech. Shown when the renderer
 * cannot start or when `?scene=0` asks for it (the economy e2e). Prices and ownership come from
 * the API and the purchase is decided there (CLAUDE.md #1); this page only asks.
 */
import { MECHS } from '@steelwake/shared/data';
import { Button, colors, spacing } from '@steelwake/ui';
import { useEffect } from 'react';
import { Link } from 'react-router';

import { MechPicker } from '../play/MechPicker.js';
import { useHangarStore } from './store.js';

const credits = (n: number) => n.toLocaleString('en-US');

export function HangarCatalog({ notice: fallbackNotice }: { notice: string | null }) {
  const view = useHangarStore((s) => s.view);
  const mech = useHangarStore((s) => s.mech);
  const load = useHangarStore((s) => s.load);
  const select = useHangarStore((s) => s.select);

  useEffect(() => {
    void load();
  }, [load]);

  const entries = new Map(view?.mechs.map((m) => [m.id, m]) ?? []);
  const balance = view?.balances.credits ?? 0;

  return (
    <div style={{ display: 'grid', justifyItems: 'center', padding: spacing.lg }}>
      {fallbackNotice ? (
        <p
          data-testid="hangar-fallback"
          role="status"
          style={{
            width: 'min(1100px, 96vw)',
            margin: `0 0 ${spacing.md}`,
            fontSize: 13,
            color: colors.textMuted,
          }}
        >
          {fallbackNotice}
        </p>
      ) : null}
      <MechPicker
        testId="hangar"
        value={mech}
        onChange={select}
        title="Hangar"
        intro={
          <>
            The five starters are yours; the rest unlock with credits earned online.{' '}
            <strong data-testid="hangar-credits" style={{ color: colors.text }}>
              Credits: {view ? credits(balance) : '…'}
            </strong>{' '}
            <Link to="/" style={{ color: colors.textMuted }}>
              Back
            </Link>
          </>
        }
        cardNote={(m) => {
          const e = entries.get(m.id);
          if (!e) return null;
          if (e.starter) return <span style={{ color: colors.accent }}>starter</span>;
          if (e.owned) return <span style={{ color: colors.success }}>owned</span>;
          return e.price !== null ? (
            <span style={{ color: colors.textMuted }}>{credits(e.price)} cr</span>
          ) : null;
        }}
        footer={<BuyFlow />}
      />
    </div>
  );
}

/** The mech buy control with its confirmation step; the same test ids on both hangar pages. */
export function BuyFlow() {
  const view = useHangarStore((s) => s.view);
  const mech = useHangarStore((s) => s.mech);
  const busy = useHangarStore((s) => s.busy);
  const error = useHangarStore((s) => s.error);
  const notice = useHangarStore((s) => s.notice);
  const confirming = useHangarStore((s) => s.confirming);
  const setConfirming = useHangarStore((s) => s.setConfirming);
  const buyMech = useHangarStore((s) => s.buyMech);
  const entry = view?.mechs.find((m) => m.id === mech) ?? null;
  const balance = view?.balances.credits ?? 0;
  const name = MECHS[mech]?.name ?? mech;
  return (
    <>
      {entry?.owned ? (
        <p data-testid="hangar-owned" style={{ fontSize: 13, color: colors.success, margin: 0 }}>
          {entry.starter ? 'A starter frame: always yours.' : 'Owned.'}
        </p>
      ) : entry?.price !== null && entry?.price !== undefined ? (
        confirming ? (
          <div style={{ display: 'grid', gap: spacing.sm }}>
            <p style={{ margin: 0, fontSize: 13 }}>
              Buy {name} for {credits(entry.price)} credits? You have {credits(balance)}.
            </p>
            <div style={{ display: 'flex', gap: spacing.sm }}>
              <Button data-testid="confirm-buy" onClick={() => void buyMech()} disabled={busy}>
                Confirm
              </Button>
              <Button
                variant="ghost"
                data-testid="cancel-buy"
                onClick={() => setConfirming(false)}
                disabled={busy}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button data-testid="buy-mech" onClick={() => setConfirming(true)} disabled={busy}>
            Buy for {credits(entry.price)} credits
          </Button>
        )
      ) : null}
      {notice ? (
        <p
          data-testid="hangar-notice"
          role="status"
          style={{ color: colors.success, fontSize: 13, margin: `${spacing.sm} 0 0` }}
        >
          {notice}
        </p>
      ) : null}
      {error ? (
        <p
          data-testid="hangar-error"
          role="alert"
          style={{ color: colors.danger, fontSize: 13, margin: `${spacing.sm} 0 0` }}
        >
          {error}
        </p>
      ) : null}
    </>
  );
}
